import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { forwardRef, type MouseEventHandler, type ReactNode } from "react";
import { useMenuCondensed } from "../../hooks/useMenuCondensed";

export interface ButtonListItemProps {
  /**
   * Icon to display in the navigation button
   */
  icon: ReactNode;
  /**
   * Text label for the navigation button
   */
  label: string;
  /**
   * Click handler for the button
   */
  onClick: MouseEventHandler<HTMLButtonElement>;
}

/**
 * A navigation button item with an icon and label
 * Designed to be used for actions like logout that require onClick handlers
 */
export const ButtonListItem = forwardRef<
  HTMLButtonElement,
  ButtonListItemProps
>(({ icon, label, onClick, ...otherProps }, ref) => {
  const isCondensed = useMenuCondensed();

  const button = (
    <ListItemButton
      component="button"
      ref={ref}
      onClick={onClick}
      sx={isCondensed ? { justifyContent: "center", px: 2 } : undefined}
      {...otherProps}
    >
      <ListItemIcon sx={isCondensed ? { minWidth: "auto" } : undefined}>
        {icon}
      </ListItemIcon>
      {!isCondensed && (
        <ListItemText
          primary={label}
          slotProps={{ primary: { noWrap: true } }}
        />
      )}
    </ListItemButton>
  );

  return (
    <ListItem disablePadding>
      {isCondensed ? (
        <Tooltip title={label} placement="right">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </ListItem>
  );
});

ButtonListItem.displayName = "ButtonListItem";
